import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './BookingPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface MeetingTypeInfo {
  name: string;
  slug: string;
  duration_minutes: number;
  description: string;
  color: string;
}

interface CalendarInfo {
  timezone: string;
  owner_name: string;
}

interface Slot {
  start_time: string;
  end_time: string;
}

interface DaySlots {
  date: string;
  slots: Slot[];
}

interface AvailableData {
  meeting_type: MeetingTypeInfo;
  calendar: CalendarInfo;
  days: DaySlots[];
}

interface BookingResult {
  id: string;
  meeting_type: string;
  date: string;
  start_time: string;
  end_time: string;
  video_link: string;
  status: string;
}

type Step = 'selecting_date' | 'selecting_time' | 'filling_form' | 'submitting' | 'confirmed' | 'cancel_confirm' | 'cancelled';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatTime12(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatDateDisplay(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

const BookingPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const pathname = window.location.pathname;

  // Detect cancel route: /book/cancel/{uuid}
  const cancelMatch = pathname.match(/\/book\/cancel\/([0-9a-f-]+)/i);
  const isCancelPage = !!cancelMatch;
  const cancelId = cancelMatch ? cancelMatch[1] : null;

  const [step, setStep] = useState<Step>(isCancelPage ? 'cancel_confirm' : 'selecting_date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AvailableData | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [result, setResult] = useState<BookingResult | null>(null);

  // Cancel page state
  const [cancelAppt, setCancelAppt] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchSlots = useCallback(async (year: number, month: number) => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/booking/${slug}/available-slots/`, {
        params: { month: `${year}-${month.toString().padStart(2, '0')}` },
      });
      setData(res.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('This booking link is not active.');
      } else {
        setError('Failed to load available times. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (!isCancelPage) {
      fetchSlots(currentMonth.year, currentMonth.month);
    }
  }, [currentMonth, fetchSlots, isCancelPage]);

  // Cancel page: fetch appointment details
  useEffect(() => {
    if (isCancelPage && cancelId) {
      setLoading(true);
      axios.get(`${API_BASE}/booking/confirm/${cancelId}/`)
        .then(res => {
          setCancelAppt(res.data);
          if (res.data.status === 'cancelled') {
            setStep('cancelled');
          }
        })
        .catch(() => setError('Appointment not found.'))
        .finally(() => setLoading(false));
    }
  }, [isCancelPage, cancelId]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 1) return { year: prev.year - 1, month: 12 };
      return { year: prev.year, month: prev.month - 1 };
    });
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 12) return { year: prev.year + 1, month: 1 };
      return { year: prev.year, month: prev.month + 1 };
    });
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const handleDateSelect = (dateStr: string) => {
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setStep('selecting_time');
  };

  const handleSlotSelect = (slot: Slot) => {
    setSelectedSlot(slot);
    setStep('filling_form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot || !slug) return;

    setStep('submitting');
    setError('');

    try {
      const res = await axios.post(`${API_BASE}/booking/${slug}/book/`, {
        date: selectedDate,
        start_time: selectedSlot.start_time,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
      });
      setResult(res.data);
      setStep('confirmed');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to book appointment. Please try again.';
      setError(msg);
      setStep('filling_form');
    }
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API_BASE}/booking/cancel/${cancelId}/`, {
        reason: cancelReason,
      });
      setStep('cancelled');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  // Calendar rendering
  const renderCalendar = () => {
    if (!data) return null;

    const { year, month } = currentMonth;
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startPad = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

    const availableDates = new Set(data.days.map(d => d.date));

    const cells: React.ReactNode[] = [];

    // Day headers
    DAY_HEADERS.forEach(d => (
      cells.push(<div key={`hdr-${d}`} className="booking-calendar-day-header">{d}</div>)
    ));

    // Empty padding
    for (let i = 0; i < startPad; i++) {
      cells.push(<div key={`pad-${i}`} className="booking-calendar-day empty" />);
    }

    // Days
    for (let d = 1; d <= totalDays; d++) {
      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
      const hasSlots = availableDates.has(dateStr);
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedDate;

      const classes = [
        'booking-calendar-day',
        hasSlots ? 'has-slots' : '',
        isToday ? 'today' : '',
        isSelected ? 'selected' : '',
      ].filter(Boolean).join(' ');

      cells.push(
        <div
          key={dateStr}
          className={classes}
          onClick={hasSlots ? () => handleDateSelect(dateStr) : undefined}
        >
          {d}
        </div>
      );
    }

    return (
      <div className="booking-calendar">
        <div className="booking-calendar-nav">
          <button onClick={handlePrevMonth}>&larr;</button>
          <h3>{MONTH_NAMES[month - 1]} {year}</h3>
          <button onClick={handleNextMonth}>&rarr;</button>
        </div>
        <div className="booking-calendar-grid">
          {cells}
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => {
    if (!data || !selectedDate) return null;

    const dayData = data.days.find(d => d.date === selectedDate);
    if (!dayData) return null;

    return (
      <div className="booking-slots">
        <h3>Available times for {formatDateDisplay(selectedDate)}</h3>
        <div className="booking-slots-grid">
          {dayData.slots.map(slot => (
            <button
              key={slot.start_time}
              className={`booking-slot-btn ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
              onClick={() => handleSlotSelect(slot)}
            >
              {formatTime12(slot.start_time)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderForm = () => {
    if (!data || !selectedDate || !selectedSlot) return null;

    return (
      <form className="booking-form" onSubmit={handleSubmit}>
        <h3>Your Details</h3>
        <div className="booking-form-summary">
          {data.meeting_type.name} &mdash; {formatDateDisplay(selectedDate)} at {formatTime12(selectedSlot.start_time)}
        </div>

        {error && <div className="booking-error">{error}</div>}

        <div className="booking-form-group">
          <label>Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Your full name"
          />
        </div>
        <div className="booking-form-group">
          <label>Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="your@email.com"
          />
        </div>
        <div className="booking-form-group">
          <label>Phone <span className="optional">(optional)</span></label>
          <input
            type="tel"
            value={formData.phone}
            onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="(555) 123-4567"
          />
        </div>
        <div className="booking-form-group">
          <label>Notes <span className="optional">(optional)</span></label>
          <textarea
            value={formData.notes}
            onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Anything you'd like to discuss?"
          />
        </div>
        <button
          type="submit"
          className="booking-submit-btn"
          disabled={step === 'submitting'}
        >
          {step === 'submitting' ? 'Booking...' : 'Confirm Booking'}
        </button>
      </form>
    );
  };

  const renderConfirmation = () => {
    if (!result || !data) return null;

    return (
      <div className="booking-confirmation">
        <div className="booking-confirmation-icon">&#10003;</div>
        <h2>Booking Confirmed!</h2>
        <p>A confirmation email has been sent to <strong>{formData.email}</strong></p>

        <div className="booking-confirmation-details">
          <div className="detail-row">
            <span className="label">Meeting</span>
            <span className="value">{result.meeting_type}</span>
          </div>
          <div className="detail-row">
            <span className="label">Date</span>
            <span className="value">{formatDateDisplay(result.date)}</span>
          </div>
          <div className="detail-row">
            <span className="label">Time</span>
            <span className="value">{formatTime12(result.start_time)} - {formatTime12(result.end_time)} PT</span>
          </div>
          <div className="detail-row">
            <span className="label">With</span>
            <span className="value">{data.calendar.owner_name}</span>
          </div>
        </div>

        {result.video_link && (
          <a href={result.video_link} className="booking-video-link" target="_blank" rel="noopener noreferrer">
            Join Video Call
          </a>
        )}
      </div>
    );
  };

  const renderCancelPage = () => {
    if (step === 'cancelled') {
      return (
        <div className="booking-confirmation">
          <div className="booking-confirmation-icon" style={{ color: '#8B0000' }}>&#10007;</div>
          <h2 style={{ color: '#8B0000' }}>Booking Cancelled</h2>
          <p>Your booking has been cancelled. You can book a new time anytime.</p>
        </div>
      );
    }

    if (!cancelAppt) return null;

    return (
      <div className="booking-cancel-form">
        <h2 style={{ marginTop: 0 }}>Cancel Booking</h2>
        <div className="booking-form-summary">
          {cancelAppt.meeting_type} &mdash; {formatDateDisplay(cancelAppt.date)} at {formatTime12(cancelAppt.start_time)}
        </div>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>
          Meeting with {cancelAppt.owner_name}
        </p>

        {error && <div className="booking-error">{error}</div>}

        <div className="booking-form-group">
          <label>Reason for cancellation <span className="optional">(optional)</span></label>
          <textarea
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            placeholder="Let us know why you're cancelling"
          />
        </div>
        <button
          className="booking-cancel-btn"
          onClick={handleCancel}
          disabled={cancelLoading}
        >
          {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="booking-page">
        <div className="booking-header">
          <h1>Red Bridge Farm Feed Co.</h1>
        </div>
        <div className="booking-content">
          <div className="booking-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !data && !cancelAppt) {
    return (
      <div className="booking-page">
        <div className="booking-header">
          <h1>Red Bridge Farm Feed Co.</h1>
        </div>
        <div className="booking-content">
          <div className="booking-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-page">
      <div className="booking-header">
        <h1>{isCancelPage ? 'Cancel Booking' : 'Book a Meeting'}</h1>
        {data && <p>with {data.calendar.owner_name}</p>}
        <div className="booking-company">Red Bridge Farm Feed Co.</div>
      </div>

      <div className="booking-content">
        {isCancelPage ? (
          renderCancelPage()
        ) : (
          <>
            {data && (
              <div className="booking-meeting-info">
                <h2>{data.meeting_type.name}</h2>
                <p className="booking-duration">{data.meeting_type.duration_minutes} minutes</p>
                {data.meeting_type.description && (
                  <p className="booking-description">{data.meeting_type.description}</p>
                )}
              </div>
            )}

            {(step === 'selecting_date' || step === 'selecting_time' || step === 'filling_form' || step === 'submitting') && (
              <>
                {step !== 'selecting_date' && (
                  <button
                    className="booking-back-btn"
                    onClick={() => {
                      if (step === 'selecting_time') {
                        setSelectedDate(null);
                        setStep('selecting_date');
                      } else {
                        setSelectedSlot(null);
                        setStep('selecting_time');
                      }
                    }}
                  >
                    &larr; Back
                  </button>
                )}

                {renderCalendar()}

                {(step === 'selecting_time' || step === 'filling_form' || step === 'submitting') && renderTimeSlots()}
                {(step === 'filling_form' || step === 'submitting') && renderForm()}
              </>
            )}

            {step === 'confirmed' && renderConfirmation()}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
